import React from 'react';
import {
    BackOfficeTrailRolePrivilegeResp,
    BackOfficeTrailRoleResp,
    BackOfficeTrailPrivilegeResp,
} from 'domain/payload/response/backOfficeTrails/BackOfficeTrailsResp';

interface BackOfficeRolePrivilegesCardProps {
    rolePrivilege: BackOfficeTrailRolePrivilegeResp | null;
    loading?: boolean;
}

const BackOfficeRolePrivilegesCard: React.FC<BackOfficeRolePrivilegesCardProps> = ({
    rolePrivilege,
    loading = false,
}) => {
    const roles: BackOfficeTrailRoleResp[] = rolePrivilege?.roles || [];
    const privileges: BackOfficeTrailPrivilegeResp[] = rolePrivilege?.privileges || [];

    const renderRoleBadge = (roleName: string, key?: any) => (
        <span
            key={key}
            className="badge me-1 mb-1 px-2.5 py-1.5 text-white"
            style={{
                backgroundColor: '#475569',
                fontSize: '11px',
                fontWeight: 500,
                borderRadius: '6px',
                letterSpacing: '0.2px',
            }}
        >
            {roleName}
        </span>
    );

    const renderPrivilegeRoles = (privilege: BackOfficeTrailPrivilegeResp) => {
        if (privilege.roles && Array.isArray(privilege.roles) && privilege.roles.length > 0) {
            return (
                <div className="d-flex flex-wrap">
                    {privilege.roles.map((r, i) =>
                        renderRoleBadge(typeof r === 'string' ? r : r.name, i)
                    )}
                </div>
            );
        }

        if (privilege.role) {
            if (Array.isArray(privilege.role)) {
                return (
                    <div className="d-flex flex-wrap">
                        {privilege.role.map((r, i) =>
                            renderRoleBadge(typeof r === 'string' ? r : r.name, i)
                        )}
                    </div>
                );
            }
            return renderRoleBadge(
                typeof privilege.role === 'string' ? privilege.role : privilege.role.name
            );
        }

        if (roles.length > 0) {
            return (
                <div className="d-flex flex-wrap">
                    {roles.map((r, i) => renderRoleBadge(r.name, i))}
                </div>
            );
        }

        return <span className="text-muted small">-</span>;
    };

    return (
        <div className="card h-100 border shadow-none">
            <div className="card-header bg-light border-bottom py-3 px-4">
                <h6 className="mb-0 fw-bold">Active Roles &amp; Privileges</h6>
            </div>
            <div className="card-body p-4 d-flex flex-column">
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="mb-4">
                            <div
                                className="text-muted small fw-bold text-uppercase mb-2"
                                style={{ letterSpacing: '0.5px' }}
                            >
                                ACTIVE ROLES
                            </div>
                            <div className="d-flex flex-wrap gap-2">
                                {roles.length > 0 ? (
                                    roles.map((role, index) =>
                                        renderRoleBadge(role.name || 'Role', index)
                                    )
                                ) : (
                                    <span className="text-muted small">No active roles</span>
                                )}
                            </div>
                        </div>

                        <div className="flex-grow-1 d-flex flex-column">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div
                                    className="text-muted small fw-bold text-uppercase"
                                    style={{ letterSpacing: '0.5px' }}
                                >
                                    ACTIVE PRIVILEGES
                                </div>
                                <span
                                    className="badge rounded-pill border bg-light text-dark fw-bold px-2 py-1"
                                    style={{ fontSize: '11px' }}
                                >
                                    {privileges.length} Privileges
                                </span>
                            </div>

                            <div
                                className="table-responsive border rounded"
                                style={{ maxHeight: '310px', overflowY: 'auto' }}
                            >
                                <table className="table table-sm table-hover align-middle mb-0">
                                    <thead className="table-light sticky-top" style={{ zIndex: 1 }}>
                                        <tr>
                                            <th className="text-muted small py-2 px-3 fw-bold" style={{ width: '25%' }}>
                                                Platform
                                            </th>
                                            <th className="text-muted small py-2 px-3 fw-bold" style={{ width: '45%' }}>
                                                Name
                                            </th>
                                            <th className="text-muted small py-2 px-3 fw-bold" style={{ width: '30%' }}>
                                                Role
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {privileges.length > 0 ? (
                                            privileges.map((item, idx) => (
                                                <tr key={`${item.id}-${idx}`}>
                                                    <td className="text-muted small px-3 py-2">
                                                        {item.platform}
                                                    </td>
                                                    <td className="fw-semibold text-dark px-3 py-2 small">
                                                        {item.name}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        {renderPrivilegeRoles(item)}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={3} className="text-center py-4 text-muted small">
                                                    No active privileges found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default BackOfficeRolePrivilegesCard;
